import React, { useRef, useState, useEffect } from "react";
import { motion } from "framer-motion";
import Button from "shared/Button/Button";
import { ClipLoader } from "react-spinners";
import { IButtonPrimaryProps } from "@/Interfaces/interface.buttons";

const MotionButton = motion.create(Button);
const MotionAnchor = motion.a;

const ButtonPrimary: React.FC<IButtonPrimaryProps> = ({
  className = "",
  disabled,
  sizeClass = "",
  loading = false,
  icon,
  text,
  children,
  href,
  ...args
}) => {
  const isDisabled = disabled || loading;
  const contentRef = useRef<HTMLSpanElement>(null);
  const [contentWidth, setContentWidth] = useState<number | null>(null);

  useEffect(() => {
    if (!loading && contentRef.current) {
      const width = contentRef.current.offsetWidth;
      setContentWidth(width);
    }
  }, [loading, text, children, icon]);

  const baseStyles =
    "inline-flex flex-wrap items-center justify-center rounded-md font-medium transition-all duration-200 overflow-hidden relative group focus:outline-none text-sm sm:text-base flex-shrink-0";
  // const gradientStyles =
  //   "bg-gradient-to-r from-purple-600 to-blue-600 text-white";

  // Use global gradient instead of local Tailwind gradient
  const gradientStyles = "bg-btn-primary text-white";

  const disabledStyles =
    "bg-gray-300 text-black cursor-not-allowed shadow-none ";

  const combinedClassName = [
    baseStyles,
    isDisabled ? disabledStyles : gradientStyles,
    sizeClass,
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const content = (
    <>
      {/* Shimmer overlay */}
      {!isDisabled && (
        <motion.span
          className="absolute left-0 top-0 h-ful w-full bg-black/10 skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-700 ease-out"
          aria-hidden
        />
      )}

      {/* Content */}
      <span
        ref={contentRef}
        className="relative z-10 flex items-center gap-1 sm:gap-2 justify-center"
        style={
          loading && contentWidth ? { width: `${contentWidth}px` } : undefined
        }
      >
        {loading ? (
          <ClipLoader loading={true} size={20} color="blue" />
        ) : (
          <>
            {icon && (
              <span className="flex-shrink-0 inline-flex items-center">
                {icon}
              </span>
            )}
            {(text || children) && (
              <span className="flex-shrink text-ellipsis inline-flex items-center gap-1">
                {text || children}
              </span>
            )}
          </>
        )}
      </span>
    </>
  );

  if (href) {
    return (
      <MotionAnchor
        href={href}
        className={combinedClassName}
        whileTap={!isDisabled ? { scale: 0.95 } : {}}
        aria-disabled={isDisabled}
        tabIndex={isDisabled ? -1 : undefined}
        onClick={(e) => {
          if (isDisabled) e.preventDefault();
        }}
        {...(args as any)}
      >
        {content}
      </MotionAnchor>
    );
  }

  return (
    <MotionButton
      className={combinedClassName}
      disabled={isDisabled}
      whileTap={!isDisabled ? { scale: 0.95 } : {}}
      {...args}
    >
      {content}
    </MotionButton>
  );
};

export default ButtonPrimary;
